<template>
  <button
    type="button"
    class="btn p-0 d-inline-flex align-items-center"
    @click="toggleOrder"
    :aria-label="ariaLabel"
    :aria-sort="ariaSort"
  >
    <span>{{ label }}</span>
    <span class="ms-1" aria-hidden="true">
      <i :class="iconClass"></i>
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  field: string;
  label: string;
  ordering: string[]; // array de cadenas: ['name', '-date', ...]
  multiColumn?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  multiColumn: true,
});

const emit = defineEmits<{
  (e: 'update:ordering', ordering: string[]): void;
}>();

/*
  Determina el estado actual del campo en el array `ordering`.
  Devuelve 'asc' | 'desc' | null
*/
const sortDirection = computed<'asc' | 'desc' | null>(() => {
  const idx = props.ordering.findIndex(
    item => item === props.field || item === `-${props.field}`
  );
  if (idx === -1) return null;
  return props.ordering[idx] === props.field ? 'asc' : 'desc';
});

const isAscending = computed(() => sortDirection.value === 'asc');
const isDescending = computed(() => sortDirection.value === 'desc');

const ariaSort = computed(() => {
  if (isAscending.value) return 'ascending';
  if (isDescending.value) return 'descending';
  return 'none';
});

const ariaLabel = computed(() => {
  if (isAscending.value) return `Ordenar ${props.label} descendente`;
  if (isDescending.value) return `Ordenar ${props.label} ascendente`;
  return `Ordenar ${props.label} ascendente`;
});

/* Clase del icono para que el template reaccione siempre correctamente */
const iconClass = computed(() => {
  if (isAscending.value) return 'bi bi-arrow-up';
  if (isDescending.value) return 'bi bi-arrow-down';
  return 'bi bi-arrow-down-up text-muted';
});

/*
  Ciclo de orden para cada click:
    none  -> asc  (añade campo)
    asc   -> desc (sustituye por '-field')
    desc  -> none (elimina el campo)
  - multiColumn = true  => mantiene otras columnas y preserva la posición del campo
  - multiColumn = false => reemplaza toda la ordering con el estado del campo (single-column)
*/
function toggleOrder() {
  const field = props.field;
  const ordering = [...props.ordering]; // copia (no mutar prop)

  const idx = ordering.findIndex(item => item === field || item === `-${field}`);
  const current = idx === -1 ? null : (ordering[idx] === field ? 'asc' : 'desc');

  if (props.multiColumn) {
    if (current === null) {
      // none -> asc: añadir al final
      ordering.push(field);
    } else if (current === 'asc') {
      // asc -> desc: reemplazar en la misma posición por '-field'
      ordering[idx] = `-${field}`;
    } else {
      // desc -> none: eliminar elemento
      ordering.splice(idx, 1);
    }
  } else {
    // single-column: mantener solo este campo (ciclo)
    if (current === null) {
      // none -> asc
      emit('update:ordering', [field]);
      return;
    } else if (current === 'asc') {
      // asc -> desc
      emit('update:ordering', [`-${field}`]);
      return;
    } else {
      // desc -> none
      emit('update:ordering', []);
      return;
    }
  }

  emit('update:ordering', [...ordering]);  // emitir copia para reactividad
}
</script>
